import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getHorizonIngestApps, getHorizonOptOutDetails } from '../api/horizonOptOutTracker';
import { ALL_HORIZON_REGIONS } from '../types/horizonOptOutTracker';
import type {
  HorizonIngestApp,
  HorizonOptOutDateRange,
  HorizonOptOutDisplayRow,
  HorizonRegionFilter,
} from '../types/horizonOptOutTracker';
import { getDefaultHorizonOptOutDateRange, getHorizonOptOutDateBounds } from '../utility/horizonOptOutTracker';

const horizonOptOutTrackerQueryKey = 'HorizonOptOutTracker';
const eventRequestBatchSize = 5;

export interface HorizonOptOutTrackerResult {
  data: HorizonOptOutDisplayRow[];
  hasDetailErrors: boolean;
  isEnriching: boolean;
  isError: boolean;
  isLoading: boolean;
  refetch: () => void;
  retryDetails: () => void;
}

interface EnrichRowsOptions {
  ingestApps: HorizonIngestApp[];
  isCancelled: () => boolean;
  onBatch: (updates: Map<string, HorizonOptOutDisplayRow>) => void;
  signal: AbortSignal;
  windowEnd: string;
  windowStart: string;
}

/** Loads opt-out details a few applications at a time so rows fill in as they resolve. */
const enrichRows = async ({ ingestApps, isCancelled, onBatch, signal, windowEnd, windowStart }: EnrichRowsOptions) => {
  for (let index = 0; index < ingestApps.length; index += eventRequestBatchSize) {
    const batch = ingestApps.slice(index, index + eventRequestBatchSize);
    const results = await Promise.allSettled(
      batch.map((ingestApp) => getHorizonOptOutDetails(ingestApp, windowStart, windowEnd, signal)),
    );
    if (isCancelled()) return;

    onBatch(
      new Map<string, HorizonOptOutDisplayRow>(
        results.map((result, resultIndex): [string, HorizonOptOutDisplayRow] => {
          const ingestApp = batch[resultIndex];
          if (result.status === 'rejected') {
            console.error('HorizonOptOutTracker detail load failed', result.reason);
            return [ingestApp.appUid, { ...ingestApp, detailStatus: 'error' as const }];
          }
          if (!result.value) return [ingestApp.appUid, { ...ingestApp, detailStatus: 'unresolved' as const }];
          return [
            ingestApp.appUid,
            {
              ...ingestApp,
              detailStatus: 'loaded' as const,
              optOutReason: result.value.optOutReason,
              optOutTimeUTC: result.value.optOutTimeUTC,
              type: result.value.type,
            },
          ];
        }),
      ),
    );
  }
};

/** Loads disqualified Horizon apps first, then progressively enriches details from the selected date range. */
export const useHorizonOptOutTracker = (
  dateRange?: HorizonOptOutDateRange,
  region: HorizonRegionFilter = ALL_HORIZON_REGIONS,
): HorizonOptOutTrackerResult => {
  const [rows, setRows] = useState<HorizonOptOutDisplayRow[]>([]);
  const [rowsFilterKey, setRowsFilterKey] = useState<string>();
  const [isEnriching, setIsEnriching] = useState(false);
  const [retryRequest, setRetryRequest] = useState<{ filterKey: string; ingestApps: HorizonIngestApp[] }>();
  const [defaultDateRange] = useState(getDefaultHorizonOptOutDateRange);
  const { endDate, startDate } = dateRange ?? defaultDateRange;
  const { windowEnd, windowStart } = getHorizonOptOutDateBounds({ endDate, startDate });
  const filterKey = `${windowStart}|${windowEnd}|${region}`;
  const ingestAppsQuery = useQuery({
    queryKey: [horizonOptOutTrackerQueryKey, windowStart, windowEnd, region],
    queryFn: async ({ signal }) => {
      try {
        return await getHorizonIngestApps(windowStart, windowEnd, region, signal);
      } catch (error) {
        console.error('HorizonOptOutTracker load failed', error);
        throw error;
      }
    },
    refetchOnWindowFocus: false,
  });

  const applyBatch = (updates: Map<string, HorizonOptOutDisplayRow>) =>
    setRows((currentRows) => currentRows.map((row) => updates.get(row.appUid) ?? row));

  useEffect(() => {
    if (!ingestAppsQuery.data) return;

    let isCancelled = false;
    const abortController = new AbortController();
    const ingestApps = ingestAppsQuery.data;
    setRows(ingestApps.map((ingestApp) => ({ ...ingestApp, detailStatus: 'loading' })));
    setRowsFilterKey(filterKey);
    setRetryRequest(undefined);
    setIsEnriching(ingestApps.length > 0);

    void enrichRows({
      ingestApps,
      isCancelled: () => isCancelled,
      onBatch: applyBatch,
      signal: abortController.signal,
      windowEnd,
      windowStart,
    }).then(() => {
      if (!isCancelled) setIsEnriching(false);
    });

    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [filterKey, ingestAppsQuery.data, windowEnd, windowStart]);

  useEffect(() => {
    if (!retryRequest || retryRequest.filterKey !== filterKey) return;

    let isCancelled = false;
    const abortController = new AbortController();
    setIsEnriching(true);

    void enrichRows({
      ingestApps: retryRequest.ingestApps,
      isCancelled: () => isCancelled,
      onBatch: applyBatch,
      signal: abortController.signal,
      windowEnd,
      windowStart,
    }).then(() => {
      if (isCancelled) return;
      setIsEnriching(false);
      setRetryRequest(undefined);
    });

    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [filterKey, retryRequest, windowEnd, windowStart]);

  const visibleRows = rowsFilterKey === filterKey ? rows : [];
  const isVisibleRangeEnriching = rowsFilterKey === filterKey && isEnriching;
  const failedRows = visibleRows.filter((row) => row.detailStatus === 'error');

  return {
    data: visibleRows,
    hasDetailErrors: failedRows.length > 0,
    isEnriching: isVisibleRangeEnriching,
    isError: ingestAppsQuery.isError && visibleRows.length === 0,
    isLoading:
      ingestAppsQuery.isLoading ||
      (ingestAppsQuery.data !== undefined && ingestAppsQuery.data.length > 0 && rowsFilterKey !== filterKey),
    refetch: () => {
      void ingestAppsQuery.refetch();
    },
    retryDetails: () => {
      if (failedRows.length === 0) return;
      setRows((currentRows) =>
        currentRows.map((row) => (row.detailStatus === 'error' ? { ...row, detailStatus: 'loading' } : row)),
      );
      setRetryRequest({ filterKey, ingestApps: failedRows });
    },
  };
};
